// Argentine provinces and a representative set of cities per province.
// Used by the public job-application form to enforce a consistent location
// taxonomy. This is NOT every city — just the most populous + capitals so
// the dropdowns stay usable. Candidates can pick the closest and add detail
// in the address field.

export const AR_PROVINCES: readonly string[] = [
  "Buenos Aires",
  "Ciudad Autónoma de Buenos Aires",
  "Catamarca",
  "Chaco",
  "Chubut",
  "Córdoba",
  "Corrientes",
  "Entre Ríos",
  "Formosa",
  "Jujuy",
  "La Pampa",
  "La Rioja",
  "Mendoza",
  "Misiones",
  "Neuquén",
  "Río Negro",
  "Salta",
  "San Juan",
  "San Luis",
  "Santa Cruz",
  "Santa Fe",
  "Santiago del Estero",
  "Tierra del Fuego",
  "Tucumán",
] as const;

export const AR_CITIES_BY_PROVINCE: Record<string, readonly string[]> = {
  "Buenos Aires": [
    "La Plata", "Mar del Plata", "Bahía Blanca", "Tandil", "Olavarría",
    "Quilmes", "La Matanza", "Lomas de Zamora", "Almirante Brown", "Avellaneda",
    "Tigre", "San Isidro", "Vicente López", "Pilar", "Escobar",
    "Moreno", "Merlo", "Morón", "Ituzaingó", "Hurlingham",
    "San Nicolás", "Pergamino", "Junín", "Azul", "Necochea",
    "Campana", "Zárate", "Luján", "Chivilcoy", "Chascomús",
  ],
  "Ciudad Autónoma de Buenos Aires": [
    "Ciudad Autónoma de Buenos Aires",
    "Palermo", "Belgrano", "Caballito", "Recoleta", "Villa Urquiza",
    "Núñez", "Villa Crespo", "Almagro", "Flores", "Boedo",
    "Puerto Madero", "San Telmo", "La Boca", "Barracas",
  ],
  "Catamarca": [
    "San Fernando del Valle de Catamarca", "Belén", "Tinogasta",
    "Andalgalá", "Santa María",
  ],
  "Chaco": [
    "Resistencia", "Barranqueras", "Presidencia Roque Sáenz Peña",
    "Villa Ángela", "Charata", "General San Martín",
  ],
  "Chubut": [
    "Rawson", "Comodoro Rivadavia", "Puerto Madryn", "Trelew",
    "Esquel", "Gaiman",
  ],
  "Córdoba": [
    "Córdoba Capital", "Villa Carlos Paz", "Río Cuarto", "Villa María",
    "San Francisco", "Alta Gracia", "Jesús María", "Bell Ville",
    "Marcos Juárez", "Río Tercero", "La Falda", "Villa Allende",
  ],
  "Corrientes": [
    "Corrientes Capital", "Goya", "Paso de los Libres", "Curuzú Cuatiá",
    "Mercedes", "Santo Tomé",
  ],
  "Entre Ríos": [
    "Paraná", "Concordia", "Gualeguaychú", "Concepción del Uruguay",
    "Gualeguay", "Victoria", "Colón",
  ],
  "Formosa": [
    "Formosa Capital", "Clorinda", "Pirané", "El Colorado",
  ],
  "Jujuy": [
    "San Salvador de Jujuy", "Palpalá", "Libertador General San Martín",
    "San Pedro", "Perico",
  ],
  "La Pampa": [
    "Santa Rosa", "General Pico", "Toay", "General Acha",
  ],
  "La Rioja": [
    "La Rioja Capital", "Chilecito", "Aimogasta", "Chamical",
  ],
  "Mendoza": [
    "Mendoza Capital", "Godoy Cruz", "Guaymallén", "Las Heras",
    "Maipú", "San Rafael", "Luján de Cuyo", "San Martín",
    "General Alvear", "Malargüe", "Tunuyán",
  ],
  "Misiones": [
    "Posadas", "Oberá", "Eldorado", "Puerto Iguazú",
    "Apóstoles", "Leandro N. Alem",
  ],
  "Neuquén": [
    "Neuquén Capital", "San Martín de los Andes", "Cutral Có",
    "Plottier", "Zapala", "Villa La Angostura", "Junín de los Andes",
  ],
  "Río Negro": [
    "Viedma", "San Carlos de Bariloche", "General Roca", "Cipolletti",
    "Villa Regina", "Allen", "El Bolsón",
  ],
  "Salta": [
    "Salta Capital", "San Ramón de la Nueva Orán", "Tartagal",
    "General Güemes", "Metán", "Cafayate",
  ],
  "San Juan": [
    "San Juan Capital", "Chimbas", "Rivadavia", "Rawson",
    "Pocito", "Caucete",
  ],
  "San Luis": [
    "San Luis Capital", "Villa Mercedes", "Merlo", "La Punta",
  ],
  "Santa Cruz": [
    "Río Gallegos", "Caleta Olivia", "El Calafate", "Pico Truncado",
    "Puerto Deseado", "Perito Moreno",
  ],
  "Santa Fe": [
    "Santa Fe Capital", "Rosario", "Rafaela", "Venado Tuerto",
    "Reconquista", "Santo Tomé", "Esperanza", "Casilda", "San Lorenzo",
    "Villa Gobernador Gálvez",
  ],
  "Santiago del Estero": [
    "Santiago del Estero Capital", "La Banda", "Termas de Río Hondo",
    "Añatuya", "Frías",
  ],
  "Tierra del Fuego": [
    "Ushuaia", "Río Grande", "Tolhuin",
  ],
  "Tucumán": [
    "San Miguel de Tucumán", "Yerba Buena", "Tafí Viejo", "Concepción",
    "Banda del Río Salí", "Aguilares", "Monteros",
  ],
};

// Common LATAM + international nationalities. Kept short on purpose — easy
// to scroll. If a candidate doesn't see theirs they can pick "Otra" (handled
// client-side as free text fallback).
export const NATIONALITIES: readonly string[] = [
  "Argentina",
  "Boliviana",
  "Brasileña",
  "Chilena",
  "Colombiana",
  "Costarricense",
  "Cubana",
  "Dominicana",
  "Ecuatoriana",
  "Española",
  "Estadounidense",
  "Guatemalteca",
  "Hondureña",
  "Italiana",
  "Mexicana",
  "Nicaragüense",
  "Panameña",
  "Paraguaya",
  "Peruana",
  "Portuguesa",
  "Puertorriqueña",
  "Salvadoreña",
  "Uruguaya",
  "Venezolana",
  "Otra",
] as const;
